import {
  AmplifyGet,
  AmplifyPost,
} from "@sunrun/sales-experience-shared/amplify-api-wrapper";
import {
  BookInstallAppointmentRequestData,
  BookInstallAppointmentResponse,
  InstallAppointment,
  SelfSchedulerRowResponse,
} from "../../amplify/backend/function/OfferExpCheckout/ts/public/types";
import { normalizeAvailableServiceSlots } from "../cap/constants/capInstallScheduling";
import { subscribeToAsyncRequest } from "utils/subscribeToAsyncRequest";
import { logger } from "providers/logger";

/** WS may return payload directly or axios-shaped { data } from S3 fetch */
const unwrapAsyncPayload = (result: unknown): unknown =>
  result && typeof result === "object" && "data" in result
    ? (result as { data: unknown }).data
    : result;

const normalizeAvailableServiceSlotsPayload = (payload: unknown) => {
  if (typeof payload === "string" && /no slots found/i.test(payload)) {
    return [];
  }
  if (Array.isArray(payload)) {
    return normalizeAvailableServiceSlots(payload);
  }
  return [];
};

const isEmptySlotsAsyncError = (err: unknown): boolean => {
  const message =
    typeof err === "string" ? err : err instanceof Error ? err.message : "";
  return /no\s*(available)?\s*(slot|appointment)/i.test(message);
};

const getInstallSelfSchedulerRow = async (
  authKey: string,
  prospectId: string,
  serviceAppointmentId: string
) => {
  return AmplifyGet<SelfSchedulerRowResponse>(
    "Get Install Self Scheduler Row",
    "OfferExpApi",
    `/prospect/${prospectId}/install/self-scheduler-row/${serviceAppointmentId}`,
    {
      headers: {
        Authorization: `Bearer ${authKey}`,
      },
    },
    logger
  );
};

const getInstallAppointment = async (
  authKey: string,
  prospectId: string,
  appointmentId: string
) => {
  return AmplifyGet<InstallAppointment>(
    "Get Install Appointment",
    "OfferExpApi",
    `/prospect/${prospectId}/install/appointment/${appointmentId}`,
    {
      headers: {
        Authorization: `Bearer ${authKey}`,
      },
    },
    logger
  );
};

type GetInstallAvailableSlotsParams = {
  authToken: string;
  authType: string;
  prospectId: string;
  serviceAppointmentId: string;
  startOffset: number;
  windowDays: number;
  consecutiveScheduling?: string;
  isComplexAppointment?: string;
  isReschedule?: string;
};

const getInstallAvailableSlots = async ({
  authToken,
  authType,
  prospectId,
  serviceAppointmentId,
  startOffset,
  windowDays,
  consecutiveScheduling,
  isComplexAppointment,
  isReschedule,
}: GetInstallAvailableSlotsParams) => {
  const qs = new URLSearchParams({
    startOffset: String(startOffset),
    windowDays: String(windowDays),
  });
  const isTrueOrFalse = (value?: string): value is "true" | "false" =>
    ["true", "false"].includes(value as "true" | "false");
  if (isTrueOrFalse(consecutiveScheduling)) {
    qs.set("consecutiveScheduling", consecutiveScheduling);
  }
  if (isTrueOrFalse(isComplexAppointment)) {
    qs.set("isComplexAppointment", isComplexAppointment);
  }
  if (isTrueOrFalse(isReschedule)) {
    qs.set("isReschedule", isReschedule);
  }

  try {
    const resp = await AmplifyGet<{ asyncRequestId: string }>(
      "Get Install Available Slots",
      "OfferExpApi",
      `/prospect/${prospectId}/install/available-slots/${serviceAppointmentId}?${qs.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "X-Auth-Type": authType,
        },
      },
      logger
    );

    const payload = unwrapAsyncPayload(
      await subscribeToAsyncRequest(authToken, resp.asyncRequestId)
    );
    return normalizeAvailableServiceSlotsPayload(payload);
  } catch (err: unknown) {
    if (isEmptySlotsAsyncError(err)) {
      return [];
    }
    throw err;
  }
};

const bookInstallAppointment = async (
  authKey: string,
  prospectId: string,
  serviceAppointmentId: string,
  requestData: BookInstallAppointmentRequestData
): Promise<BookInstallAppointmentResponse> => {
  const resp = await AmplifyPost<{ asyncRequestId: string }>(
    "Book Install Appointment",
    "OfferExpApi",
    `/prospect/${prospectId}/install/book/${serviceAppointmentId}`,
    {
      headers: {
        Authorization: `Bearer ${authKey}`,
      },
      body: requestData,
    },
    logger
  );
  const payload = unwrapAsyncPayload(
    await subscribeToAsyncRequest(authKey, resp.asyncRequestId)
  );
  return payload as BookInstallAppointmentResponse;
};

export {
  bookInstallAppointment,
  getInstallAppointment,
  getInstallAvailableSlots,
  getInstallSelfSchedulerRow,
};
export type { GetInstallAvailableSlotsParams };
